<template>
  <div id="router" class="h-[calc(100% - 40px)]">
    <div class="flex">
      <div class="flex-1">
        <div class="h-screen max-h-[calc(100vh-50px)] flex flex-col">
          <!-- Global Toolbar -->
           <div class="items-center hidden config mv-config sm:flex">
            <svg class="w-4 h-4 mr-2 mt-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" id="Style-Two-Pin-Marker--Streamline-Ultimate">
              <path d="M12 0a9.51 9.51 0 0 0 -9.5 9.5C2.5 16.05 9 22 11 23.65a1.49 1.49 0 0 0 1.92 0C15 22 21.5 16.05 21.5 9.5A9.51 9.51 0 0 0 12 0Zm0 14.5a5 5 0 1 1 5 -5 5 5 0 0 1 -5 5Z" fill="currentColor" stroke-width="1"></path>
            </svg>
            <select
                v-model="selectedLocation"
                class="w-40 h-7 mt-1 -ml-6 pl-6 text-xs bg-transparent border-none border-slate-600/20 rounded px-2 text-slate-200 focus:outline-none focus:border-blue-500 appearance-none cursor-pointer"
              >
                <option value="">All Locations</option>
                <option
                  v-for="loc in availableLocations"
                  :key="loc"
                  :value="loc"
                >
                  {{ loc }}
                </option>
              </select>
           </div>
          <div class="flex-1 flex flex-col min-h-0">
          <!-- Inputs Panel -->
          <div class="h-[65%]">
            <div class="h-full flex flex-col">
              <!-- Inputs Header -->
              <div
                class="flex items-center justify-between px-5 py-2.5 text-sm uppercase font-medium tracking-wide text-blue-300"
              >
                <div class="flex items-center space-x-4">
                  <div class="flex items-center">
                    <i class="bi bly-output mr-2"></i>
                    <span>Inputs ({{ filteredInputs.length }})</span>
                  </div>
                  <span v-show="loading" class="ml-2">
                    <i class="bi bly-loading text-red-500 animate-spin"></i>
                  </span>

                </div>
                <div class="flex items-center space-x-3">
                  <div v-if="!isMobileOnly" class="w-48">
                    <el-input
                      placeholder="Search inputs..."
                      size="small"
                      clearable
                      @change="searchInputChanged"
                      v-model="searchInput"
                    >
                      <i slot="suffix" class="el-input__icon el-icon-search"></i>
                    </el-input>
                  </div>
                </div>
              </div>

              <!-- Inputs List -->
              <div v-bar class="flex-1 inputss">
                <div class="p-0.5 pb-12 inputs">
                  <RouterInputItem
                    v-for="(input, index) in filteredInputs"
                    :key="input.id"
                    :input="input"
                    :index="index"
                    :selected-input="selectedInput"
                    :selected-outputs="selectedOutputs"
                    :active-output-input="activeOutputInput"
                    :outputs="outputs"
                    :input-color="getInputColor(input)"
                    :has-permissions="isAdmin || acl.ro || acl.clientAdmin"
                    :show-take-id="showTakeId"
                    :loading="loading"
                    :is-routing-loading="isInputLoading(input.id)"
                    :thumbnail-data="getThumbnailData(input)"
                    @route-input="routeInput"
                    @show-take="(inputId) => showTakeId = inputId"
                    @confirm-take="endRouteCommand"
                    @input-operation="handleInputOperation"
                    @disconnect-all-routes="handleDisconnectAllRoutes"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Outputs Panel -->
          <div class="h-[35%] bg-black bg-opacity-30">
            <div class="h-full flex flex-col">
              <!-- Outputs Header -->
              <div
                class="flex items-center justify-between px-5 py-2.5 text-sm uppercase font-medium tracking-wide text-green-400"
              >
                <div class="flex items-center">
                  <i class="bi bly-input mr-2"></i>
                  Outputs
                  <span v-show="loading" class="ml-2">
                    <i class="bi bly-loading text-red-500 animate-spin"></i>
                  </span>
                </div>
                <div class="flex items-center space-x-3">
                  <div v-if="!isMobileOnly" class="w-48">
                    <el-input
                      placeholder="Search outputs..."
                      size="small"
                      clearable
                      @change="searchOutputChanged"
                      v-model="searchOutput"
                    >
                      <i slot="suffix" class="el-input__icon el-icon-search"></i>
                    </el-input>
                  </div>
                </div>
              </div>

              <!-- Outputs List -->
              <div
                v-bar
                class="flex-1 relative"
                @mousedown="startDragSelect"
                @mousemove="updateDragSelect"
                @mouseup="endDragSelect"
                @mouseleave="cancelDragSelect"
              >
                <div class="p-0.5 pb-12 relative ">
                  <!-- Drag Selection Rectangle -->
                  <div
                    v-show="isDragging"
                    class="fixed border-2 border-dashed border-blue-400 bg-blue-400 bg-opacity-5 z-25 pointer-events-none rounded-xs backdrop-blur-xs"
                    :style="dragSelectStyle"
                  ></div>

                  <div class="flex! flex-wrap justify-start items-start gap-2">
                    <RouterOutputItem
                      v-for="(output, index) in filteredOutputs"
                      :key="output.id"
                      :ref="`output-${output.id}`"
                      :output="output"
                      :index="index"
                      :selected-output="selectedOutput"
                      :selected-output-ids="selectedOutputIds"
                      :active-input-outputs="activeInputOutputs"
                      :output-color="getOutputColor(output)"
                      :has-permissions="isAdmin || acl.ro || acl.clientAdmin"
                      :is-routing-loading="isOutputLoading(output.id)"
                      @output-mousedown="handleOutputMouseDown"
                      @output-click="handleOutputClick"
                      @ott-operation="handleOttOperation"
                      @route-operation="handleRouteOperation"
                      @show-stream-key-dialog="showStreamKeyDialogForOutput"
                      @disconnect-route="handleDisconnectRoute"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Live Event Warning Modal -->
    <el-dialog
      title="Live Event Conflict Warning"
      :visible.sync="showLiveEventWarning"
      width="500px"
      :before-close="cancelLiveEventOverride"
    >
      <div>
        <el-alert
          title="Warning"
          type="warning"
          :description="liveEventWarningMessage"
          :closable="false"
          show-icon
        >
        </el-alert>
        <div style="margin-top: 20px">
          <p>Do you want to proceed anyway? This action may affect live broadcasts.</p>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="cancelLiveEventOverride">Cancel</el-button>
        <el-button type="danger" @click="confirmLiveEventOverride">Proceed</el-button>
      </span>
    </el-dialog>

    <!-- Stream Key Input Modal -->
    <el-dialog
      title="Set Stream Key"
      :visible.sync="showStreamKeyDialog"
      width="400px"
      :before-close="cancelStreamKey"
    >
      <el-form>
        <el-form-item label="Stream Key:">
          <el-input
            v-model="streamKeyInputValue"
            placeholder="Enter stream key..."
            clearable
            @keyup.enter="confirmStreamKey"
          >
          </el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="cancelStreamKey">Cancel</el-button>
        <el-button type="primary" @click="confirmStreamKey" :disabled="!streamKeyInputValue"
          >Confirm</el-button
        >
      </span>
    </el-dialog>

    <!-- Help Configuration -->
    <div class="fixed bottom-4 right-4">
      <el-tooltip content="Help" placement="left">
        <el-popover placement="top-start" width="400" trigger="hover">
          <el-alert :closable="false" class="mb-3">
            Current state refreshes every 15 seconds
          </el-alert>
          <h3 class="font-bold mb-2">Manual Routing Operations</h3>
          <div class="space-y-2 text-sm mb-4">
            <div>
              <strong>OTT Outputs:</strong> Start/Stop targets, restart applications, set stream
              keys, disconnect single routes
            </div>
            <div><strong>ROUTE Outputs:</strong> Start/Stop interfaces, disconnect single routes</div>
            <div><strong>Inputs:</strong> TAKE actions to route, restart encoders, ⚠️ disconnect ALL routes (bulk action)</div>
            <div>
              <strong>Live Events:</strong> Warnings shown for operations that may affect live
              broadcasts
            </div>
            <div>
              <strong>Action Controls:</strong> Hover over items to reveal operation buttons
            </div>
          </div>
          <h3 class="font-bold mb-2">Keybindings</h3>
          <div class="space-y-1 text-sm">
            <div>Hold <el-tag size="small">SHIFT</el-tag> to disable scrolling.</div>
            <div>
              Hold <el-tag size="small">CMD</el-tag> (Mac) or
              <el-tag size="small">CTRL</el-tag> (Windows) to select multiple outputs.
            </div>
            <div>Click and drag to select multiple outputs.</div>
            <div>Click input to route to selected outputs with manual routing.</div>
          </div>
          <i
            slot="reference"
            class="bi bly-question text-lg cursor-pointer hover:text-blue-400 transition-colors"
          ></i>
        </el-popover>
      </el-tooltip>
    </div>
  </div>
</template>

<script>
/**
 * RouterAlt Vue Component
 * 
 * This component manages the router interface with inputs and outputs.
 * Individual input and output items have been extracted into separate
 * components (RouterInputItem and RouterOutputItem) for better maintainability.
 * 
 * Key Features:
 * - Manual routing operations for OTT and ROUTE outputs
 * - Input encoder management 
 * - Live event conflict detection
 * - Drag selection for multiple outputs
 * - Responsive design with hover-based action controls
 */
import { mapState, mapActions, mapMutations } from "vuex";
import { isMobileOnly } from "mobile-device-detect";
import _ from "lodash";
import ColorGenerator from "@/services/ColorGenerator";
import RouterInputItem from "@/components/router/RouterInputItem.vue";
import RouterOutputItem from "@/components/router/RouterOutputItem.vue";

export default {
  name: "RouterAlt",
  components: { 
    RouterInputItem,
    RouterOutputItem
  },

  data() {
    return {
      isMobileOnly,
      useTake: true,
      showTakeId: null,

      // Routing state
      selectedInput: null,
      selectedOutput: null,
      selectedOutputIds: [],

      // Drag selection
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      dragEndX: 0,
      dragEndY: 0,

      // Location filtering
      selectedLocation: "",

      // Search and UI state
      keys: {},
      searchOutput: "",
      searchInput: "",
      interval: null,

      // Manual routing state
      showStreamKeyDialog: false,
      streamKeyInputValue: "",
      pendingRouteOperation: null,
      showLiveEventWarning: false,
      liveEventWarningMessage: "",

      // Thumbnail management
      inputThumbnails: {}, // Store thumbnails: { inputId: { data: base64String, timestamp: Date, loading: boolean, error: string } }
      thumbnailRefreshInterval: null,
      thumbnailRefreshDelay: 15000, // 15 seconds in milliseconds
      thumbnailBatchLoading: false,
      lastThumbnailFetch: null,

      // Individual loading states for routing operations
      inputLoadingStates: {}, // Track loading state for each input: { inputId: boolean }
      outputLoadingStates: {}, // Track loading state for each output: { outputId: boolean }
    };
  },

  created() {
    this.init();
    this.initInterval();
    this.bindKeyboardEvents();
  },

  beforeDestroy() {
    this.cleanup();
  },

  computed: {
    ...mapState({
      acl: (state) => state.userAccount.aclCurrent,
      isAdmin: (state) => state.userAccount.isAdmin,
      isSysAdmin: (state) => state.userAccount.isSysAdmin,
      client: (state) => state.userAccount.client,
      account: (state) => state.userAccount.account,
      loading: (state) => state.routerAlt.loading,
      inputs: (state) => state.routerAlt.inputs,
      outputs: (state) => state.routerAlt.outputs,
    }),

    availableLocations() {
      const locations = new Set();

      // Collect locations from inputs
      this.inputs.forEach((input) => {
        if (!input.location) return;

        let inputLocs = [];
        try {
          if (typeof input.location === "string") {
            const trimmed = input.location.trim();
            if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
              inputLocs = JSON.parse(trimmed);
            } else {
              inputLocs = [trimmed];
            }
          } else if (Array.isArray(input.location)) {
            inputLocs = input.location;
          }
        } catch (e) {
          inputLocs = [String(input.location)];
        }

        inputLocs.forEach((loc) => {
          if (loc && loc !== "DUMMY") locations.add(loc);
        });
      });

      // Collect locations from outputs
      this.outputs.forEach((output) => {
        if (output.location && output.location !== "DUMMY") {
          locations.add(output.location);
        }
      });

      return Array.from(locations).sort();
    },

    accountId() {
      return this.account.code;
    },

    clientId() {
      return this.client.code;
    },

    claimsCanEdit() {
      return [`ro.${this.accountId}.${this.clientId}`];
    },

    claimsCanAccess() {
      return [`rv.${this.accountId}.${this.clientId}`, `ro.${this.accountId}.${this.clientId}`];
    },

    originRouterColor() {
      return (id) => (id ? ColorGenerator.generateNewColor(id) : "#374151"); // muted gray for unrouted
    },

    // Get color for an output based on its routed input
    getOutputColor() {
      return (output) => {
        if (output.routedInput?.id) {
          return ColorGenerator.generateNewColor(output.routedInput.id);
        }
        return "#374151"; // muted gray for unrouted outputs
      };
    },

    // Get color for an input - matches the color used by connected outputs
    getInputColor() {
      return (input) => {
        // Check if this input is routed to any outputs
        const hasConnectedOutputs = this.outputs.some(
          (output) => output.routedInput?.id === input.id
        );

        if (hasConnectedOutputs) {
          return ColorGenerator.generateNewColor(input.id);
        }
        return "#374151"; // muted gray for unrouted inputs
      };
    },

    activeInputOutputs() {
      return this.selectedInput
        ? this.outputs
            .filter((output) => output.routedInput?.id === this.selectedInput.id)
            .map((output) => output.id)
        : [];
    },

    activeOutputInput() {
      if (this.selectedOutput && this.selectedOutput.routedInput) {
        return this.selectedOutput.routedInput;
      }
      return {};
    },

    filteredInputs() {
      let inputs = this.inputs;

      // Filter by search
      if (this.searchInput.length > 2) {
        const searchTerm = this.searchInput.toLowerCase();
        inputs = inputs.filter((item) => item && item.name.toLowerCase().includes(searchTerm));
      }

      // Filter by location
      if (this.selectedLocation) {
        inputs = inputs.filter((input) => {
          if (!input.location) return false;
          
          let inputLocs = [];
          try {
            if (typeof input.location === "string") {
              const trimmed = input.location.trim();
              if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
                inputLocs = JSON.parse(trimmed);
              } else {
                inputLocs = [trimmed];
              }
            } else if (Array.isArray(input.location)) {
              inputLocs = input.location;
            }
          } catch (e) {
            return false;
          }

          return inputLocs.includes(this.selectedLocation);
        });
      }

      return inputs.sort((a, b) => a.name.localeCompare(b.name));
    },

    filteredOutputs() {
      let outputs = this.outputs;

      // Filter by outputType (only OTT and ROUTE)
      outputs = outputs.filter(
        (output) => output.outputType === "OTT" || output.outputType === "ROUTE" || output.outputType === "TECHEX"
      );

      // Filter by search
      if (this.searchOutput.length > 2) {
        const searchTerm = this.searchOutput.toLowerCase();
        outputs = outputs.filter((item) => item.name.toLowerCase().includes(searchTerm));
      }

      // Filter by location
      if (this.selectedLocation) {
        outputs = outputs.filter((output) => output.location === this.selectedLocation);
      }

      return outputs.sort((a, b) => a.name.localeCompare(b.name));
    },

    selectedOutputs() {
      if (this.selectedOutputIds.length) {
        return this.filteredOutputs.filter((output) => this.selectedOutputIds.includes(output.id));
      } else if (this.selectedOutput) {
        return [this.selectedOutput];
      }
      return null;
    },

    dragSelectStyle() {
      const left = Math.min(this.dragStartX, this.dragEndX);
      const top = Math.min(this.dragStartY, this.dragEndY);
      const width = Math.abs(this.dragEndX - this.dragStartX);
      const height = Math.abs(this.dragEndY - this.dragStartY);

      return {
        left: `${left}px`,
        top: `${top}px`,
        width: `${width}px`,
        height: `${height}px`,
      };
    },
  },
  watch: {
    searchOutput() {
      if (this.searchOutput.length > 2) {
        this.clearOutputsSelection();
      }
    },
  },

  methods: {
    ...mapActions({
      fetchInputs: "routerAlt/fetchInputs",
      fetchOutputs: "routerAlt/fetchOutputs",
      routerStartTarget: "routerAlt/routerStartTarget",
      routerStopTarget: "routerAlt/routerStopTarget",
      routerSetStreamKey: "routerAlt/routerSetStreamKey",
      // Manual routing mutations
      routerRoute: "routerAlt/routerRoute",
      routerRouteSetStreamKey: "routerAlt/routerRouteSetStreamKey",
      routerRouteStartTarget: "routerAlt/routerRouteStartTarget",
      routerRouteStopTarget: "routerAlt/routerRouteStopTarget",
      routerRouteRestartApplication: "routerAlt/routerRouteRestartApplication",
      routerRouteStartInterface: "routerAlt/routerRouteStartInterface",
      routerRouteStopInterface: "routerAlt/routerRouteStopInterface",
      routerRouteRestartEncoder: "routerAlt/routerRouteRestartEncoder",
      // New mutation for disconnecting routes
      routerRouteOut: "routerAlt/routerRouteOut",
      fetchRouterThumbnails: "routerAlt/fetchRouterThumbnails",
    }),

    // Live event conflict detection
    async checkLiveEventConflict(output) {
      // Check if output is associated with a LIVE event
      // This would need to be implemented based on your data structure
      // For now, we'll assume outputs have an 'events' property
      const hasLiveEvent = output.events?.items?.some(
        (eventMap) => eventMap.event?.status === "LIVE"
      );

      if (hasLiveEvent) {
        return {
          hasConflict: true,
          message: `Output "${output.name}" is currently associated with a LIVE event. Proceeding will affect the live broadcast.`,
        };
      }

      return { hasConflict: false };
    },

    // Location validation methods
    /**
     * Check if input and output locations are compatible for routing
     * @param {Object} input - Input object with location array
     * @param {Object} output - Output object with location string
     * @returns {Object} Validation result with isValid and reason
     */
    validateInputOutputLocations(input, output) {
      // TECHEX specific validation
      if (output.outputType === "TECHEX") {
        try {
          const inputConfig = typeof input.config === "string" ? JSON.parse(input.config) : input.config;
          const outputConfig = typeof output.config === "string" ? JSON.parse(output.config) : output.config;

          if (inputConfig?.techexEdgeId && outputConfig?.techexEdgeId) {
            if (inputConfig.techexEdgeId !== outputConfig.techexEdgeId) {
              return {
                isValid: false,
                reason: `TECHEX Edge ID mismatch: Input (${inputConfig.techexEdgeId}) vs Output (${outputConfig.techexEdgeId})`,
              };
            }
          }
        } catch (e) {
          console.warn("Failed to parse TECHEX config", e);
        }
      }

      // Skip validation if either location is undefined, null, or empty
      if (!input.location || !output.location || 
          input.location === '' || output.location === '') {
        return { 
          isValid: true, 
          reason: 'No location constraints - validation skipped' 
        };
      }

      // Parse input.location - handle both stringified arrays and regular strings
      let inputLocations = [];
      try {
        if (typeof input.location === 'string') {
          // Try to parse as JSON first (for stringified arrays)
          if (input.location.trim().startsWith('[') && input.location.trim().endsWith(']')) {
            inputLocations = JSON.parse(input.location);
          } else {
            // Single string location
            inputLocations = [input.location];
          }
        } else if (Array.isArray(input.location)) {
          // Already an array
          inputLocations = input.location;
        } else {
          // Fallback - convert to array
          inputLocations = [input.location];
        }
      } catch (parseError) {
        console.warn('Failed to parse input.location, treating as single string:', input.location, parseError);
        inputLocations = [input.location];
      }

      // Ensure we have a valid array
      if (!Array.isArray(inputLocations) || inputLocations.length === 0) {
        return { 
          isValid: true, 
          reason: 'Invalid input location format - validation skipped' 
        };
      }

      // Handle DUMMY placeholder locations
      if (inputLocations.includes('DUMMY') || output.location === 'DUMMY') {
        return { 
          isValid: true, 
          reason: 'DUMMY location detected - validation skipped' 
        };
      }

      // Check if output location exists in input location array
      const isLocationMatch = inputLocations.includes(output.location);

      if (!isLocationMatch) {
        return {
          isValid: false,
          reason: `Location mismatch: Input locations [${inputLocations.join(', ')}] do not include output location "${output.location}"`
        };
      }

      return { 
        isValid: true, 
        reason: `Location match confirmed: "${output.location}"` 
      };
    },

    /**
     * Filter inputs that are compatible with a given output location
     * @param {Object} output - Output object to check compatibility against
     * @returns {Array} Array of compatible inputs
     */
    getCompatibleInputsForOutput(output) {
      return this.filteredInputs.filter(input => {
        const validation = this.validateInputOutputLocations(input, output);
        return validation.isValid;
      });
    },

    /**
     * Filter outputs that are compatible with a given input location
     * @param {Object} input - Input object to check compatibility against  
     * @returns {Array} Array of compatible outputs
     */
    getCompatibleOutputsForInput(input) {
      return this.filteredOutputs.filter(output => {
        const validation = this.validateInputOutputLocations(input, output);
        return validation.isValid;
      });
    },

    // Loading state management methods
    setInputLoading(inputId, loading = true) {
      this.$set(this.inputLoadingStates, inputId, loading);
    },

    setOutputLoading(outputId, loading = true) {
      this.$set(this.outputLoadingStates, outputId, loading);
    },

    isInputLoading(inputId) {
      return !!this.inputLoadingStates[inputId];
    },

    isOutputLoading(outputId) {
      return !!this.outputLoadingStates[outputId];
    },

    clearAllLoadingStates() {
      this.inputLoadingStates = {};
      this.outputLoadingStates = {};
    },

    // Manual routing operations
    async executeManualRoute(input, output, options = {}) {
      const hasPermission = this.isAdmin || this.acl.clientAdmin || this.acl.ro;
      if (!hasPermission) {
        this.$message.error("Insufficient permissions to perform routing");
        return;
      }

      try {
        // Validate input-output location compatibility
        const locationValidation = this.validateInputOutputLocations(input, output);
        if (!locationValidation.isValid) {
          this.$message.error(`Cannot route: ${locationValidation.reason}`);
          console.warn('Location validation failed:', locationValidation);
          return;
        }

        console.log('Location validation passed:', locationValidation.reason);

        // Check for live event conflict
        const conflictCheck = await this.checkLiveEventConflict(output);
        if (conflictCheck.hasConflict) {
          this.liveEventWarningMessage = conflictCheck.message;
          this.showLiveEventWarning = true;
          this.pendingRouteOperation = { input, output, options };
          return;
        }

        await this.performRouting(input, output, options);
      } catch (error) {
        console.error("Manual routing error:", error);
        this.$message.error("Failed to execute manual routing");
      }
    },

    async performRouting(input, output, options = {}) {
      const { audioPid, videoPid, streamKey } = options;

      // Set loading states for input and output
      this.setInputLoading(input.id, true);
      this.setOutputLoading(output.id, true);

      try {
        const result = await this.routerRoute({
          inputId: input.id,
          outputId: output.id,
          audioPid,
          videoPid,
          streamKey,
        });

        if (result.success) {
          this.$message.success(result.message || "Route established successfully");
          // Refresh data to show updated connections
          await this.fetchOutputs();
        } else {
          this.$message.error(result.message || "Failed to establish route");
        }
      } finally {
        // Clear loading states regardless of success/failure
        this.setInputLoading(input.id, false);
        this.setOutputLoading(output.id, false);
      }
    },

    // New method for disconnecting routes using routerRouteOut mutation
    async disconnectRoute(input, output) {
      const hasPermission = this.isAdmin || this.acl.clientAdmin || this.acl.ro;
      if (!hasPermission) {
        this.$message.error("Insufficient permissions to disconnect route");
        return;
      }

      try {
        // Show confirmation dialog for disconnection
        await this.$confirm(
          `Are you sure you want to disconnect the route from "${input.name}" to "${output.name}"?`,
          'Confirm Route Disconnection',
          {
            confirmButtonText: 'Disconnect',
            cancelButtonText: 'Cancel',
            type: 'warning',
            confirmButtonClass: 'el-button--danger'
          }
        );

        // Check for live event conflict
        const conflictCheck = await this.checkLiveEventConflict(output);
        if (conflictCheck.hasConflict) {
          this.liveEventWarningMessage = `${conflictCheck.message} Disconnecting may affect the live broadcast.`;
          this.showLiveEventWarning = true;
          this.pendingRouteOperation = {
            input,
            output,
            operation: () => this.executeRouteDisconnection(input, output)
          };
          return;
        }

        await this.executeRouteDisconnection(input, output);
      } catch (error) {
        if (error !== 'cancel') {
          console.error("Route disconnection error:", error);
          this.$message.error("Failed to disconnect route");
        }
      }
    },

    async executeRouteDisconnection(input, output) {
      // Set loading states for input and output
      this.setInputLoading(input.id, true);
      this.setOutputLoading(output.id, true);

      try {
        // For output-level disconnection, only send outputId
        const result = await this.routerRouteOut({
          outputId: output.id
          // Note: inputId is intentionally omitted for output-specific disconnects
        });

        if (result.success) {
          this.$message.success(result.message || `Route disconnected: ${input.name} → ${output.name}`);
          // Refresh data to show updated connections
          await Promise.all([this.fetchInputs(), this.fetchOutputs()]);
        } else {
          this.$message.error(result.message || "Failed to disconnect route");
        }
      } catch (error) {
        console.error("Execute route disconnection error:", error);
        this.$message.error("Failed to disconnect route");
      } finally {
        // Clear loading states regardless of success/failure
        this.setInputLoading(input.id, false);
        this.setOutputLoading(output.id, false);
      }
    },

    // OTT output operations
    async handleOttOperation(output, operation) {
      const hasPermission = this.isAdmin || this.acl.clientAdmin || this.acl.ro;
      if (!hasPermission) {
        this.$message.error("Insufficient permissions to perform operation");
        return;
      }

      try {
        // Check for live event conflict
        const conflictCheck = await this.checkLiveEventConflict(output);
        if (conflictCheck.hasConflict) {
          this.liveEventWarningMessage = conflictCheck.message;
          this.showLiveEventWarning = true;
          this.pendingRouteOperation = {
            output,
            operation: () => this.executeOttOperation(output, operation),
          };
          return;
        }

        await this.executeOttOperation(output, operation);
      } catch (error) {
        console.error("OTT operation error:", error);
        this.$message.error("Failed to execute OTT operation");
      }
    },

    async executeOttOperation(output, operation) {
      // Set loading state for the output
      this.setOutputLoading(output.id, true);

      try {
        let result;

        switch (operation) {
          case "start":
            result = await this.routerRouteStartTarget({ outputId: output.id });
            break;
          case "stop":
            result = await this.routerRouteStopTarget({ outputId: output.id });
            break;
          case "restart":
            result = await this.routerRouteRestartApplication({ outputId: output.id });
            break;
          case "setStreamKey":
            if (!this.streamKeyInputValue) {
              this.$message.error("Stream key is required");
              return;
            }
            result = await this.routerRouteSetStreamKey({
              outputId: output.id,
              streamKey: this.streamKeyInputValue,
            });
            break;
          default:
            this.$message.error("Unknown operation");
            return;
        }

        if (result.success) {
          this.$message.success(result.message || "Operation completed successfully");
          await this.fetchOutputs();
        } else {
          this.$message.error(result.message || "Operation failed");
        }
      } finally {
        // Clear loading state regardless of success/failure
        this.setOutputLoading(output.id, false);
      }
    },

    // Stream router interface operations
    async handleRouteOperation(output, operation) {
      const hasPermission = this.isAdmin || this.acl.clientAdmin || this.acl.ro;
      if (!hasPermission) {
        this.$message.error("Insufficient permissions to perform operation");
        return;
      }

      try {
        // Check for live event conflict
        const conflictCheck = await this.checkLiveEventConflict(output);
        if (conflictCheck.hasConflict) {
          this.liveEventWarningMessage = conflictCheck.message;
          this.showLiveEventWarning = true;
          this.pendingRouteOperation = {
            output,
            operation: () => this.executeRouteOperation(output, operation),
          };
          return;
        }

        await this.executeRouteOperation(output, operation);
      } catch (error) {
        console.error("Route operation error:", error);
        this.$message.error("Failed to execute route operation");
      }
    },

    async executeRouteOperation(output, operation) {
      // Set loading state for the output
      this.setOutputLoading(output.id, true);

      try {
        let result;

        switch (operation) {
          case "startInterface":
            result = await this.routerRouteStartInterface({ outputId: output.id });
            break;
          case "stopInterface":
            result = await this.routerRouteStopInterface({ outputId: output.id });
            break;
          case "disconnect":
            if (!output.routedInput) {
              this.$message.warning("No route to disconnect for this output");
              return;
            }
            await this.handleDisconnectRoute(output);
            return; // handleDisconnectRoute manages its own success/error messaging and loading states
          default:
            this.$message.error("Unknown operation");
            return;
        }

        if (result.success) {
          this.$message.success(result.message || "Operation completed successfully");
          await this.fetchOutputs();
        } else {
          this.$message.error(result.message || "Operation failed");
        }
      } finally {
        // Clear loading state regardless of success/failure (except for disconnect which handles its own)
        if (operation !== "disconnect") {
          this.setOutputLoading(output.id, false);
        }
      }
    },

    // Input operations
    async handleInputOperation(input, operation) {
      const hasPermission = this.isAdmin || this.acl.clientAdmin || this.acl.ro;
      if (!hasPermission) {
        this.$message.error("Insufficient permissions to perform operation");
        return;
      }

      try {
        // Set loading state for the input
        this.setInputLoading(input.id, true);

        let result;

        switch (operation) {
          case "restartEncoder":
            result = await this.routerRouteRestartEncoder({ inputId: input.id });
            break;
          default:
            this.$message.error("Unknown operation");
            return;
        }

        if (result.success) {
          this.$message.success(result.message || "Operation completed successfully");
          await this.fetchInputs();
        } else {
          this.$message.error(result.message || "Operation failed");
        }
      } catch (error) {
        console.error("Input operation error:", error);
        this.$message.error("Failed to execute input operation");
      } finally {
        // Clear loading state regardless of success/failure
        this.setInputLoading(input.id, false);
      }
    },

    // Live event warning handlers
    async confirmLiveEventOverride() {
      this.showLiveEventWarning = false;

      if (this.pendingRouteOperation) {
        if (this.pendingRouteOperation.operation) {
          await this.pendingRouteOperation.operation();
        } else {
          await this.performRouting(
            this.pendingRouteOperation.input,
            this.pendingRouteOperation.output,
            this.pendingRouteOperation.options
          );
        }
        this.pendingRouteOperation = null;
      }
    },

    cancelLiveEventOverride() {
      this.showLiveEventWarning = false;
      this.pendingRouteOperation = null;
    },

    // Stream key dialog handlers
    showStreamKeyDialogForOutput(output) {
      this.showStreamKeyDialog = true;
      this.streamKeyInputValue = "";
      this.pendingRouteOperation = { output };
    },

    async confirmStreamKey() {
      if (this.pendingRouteOperation) {
        await this.executeOttOperation(this.pendingRouteOperation.output, "setStreamKey");
        this.pendingRouteOperation = null;
      }
      this.showStreamKeyDialog = false;
    },

    cancelStreamKey() {
      this.showStreamKeyDialog = false;
      this.streamKeyInputValue = "";
      this.pendingRouteOperation = null;
    },

    // Lifecycle methods
    bindKeyboardEvents() {
      window.addEventListener("keyup", this.keyUp);
      window.addEventListener("keydown", this.keyDown);
    },

    cleanup() {
      if (this.interval) {
        clearInterval(this.interval);
        this.interval = null;
      }
      if (this.thumbnailRefreshInterval) {
        clearInterval(this.thumbnailRefreshInterval);
        this.thumbnailRefreshInterval = null;
      }
      // Clear all loading states on cleanup
      this.clearAllLoadingStates();
      window.removeEventListener("keyup", this.keyUp);
      window.removeEventListener("keydown", this.keyDown);
    },

    // Keyboard events
    keyDown(e) {
      this.keys[e.key] = true;
    },

    keyUp(e) {
      this.keys[e.key] = false;
    },

    // Initialization
    initInterval() {
      this.interval = setInterval(async () => {
        await Promise.all([this.fetchOutputs(), this.fetchInputs()]);

        // Update selected output if it exists
        if (this.selectedOutput) {
          const updatedOutput = this.filteredOutputs.find(
            (output) => output.id === this.selectedOutput.id
          );
          if (updatedOutput) {
            this.selectedOutput.routedInput = updatedOutput.routedInput;
          }
        }

        this.$forceUpdate();
      }, 15000);

      // Initialize thumbnail refresh interval
      this.initThumbnailRefresh();
    },

    async init() {
      // Restore saved state from localStorage
      this.searchOutput = localStorage.getItem("router_searchOutput") || "";
      this.searchInput = localStorage.getItem("router_searchInput") || "";

      // Clear any stale loading states
      this.clearAllLoadingStates();

      try {
        await Promise.all([this.fetchOutputs(), this.fetchInputs()]);
      } catch (error) {
        this.$message({
          showClose: true,
          message: "Oops, something went wrong.",
          type: "error",
        });
      }
    },

    // Routing logic
    routeInput(input, outputs) {
      const hasPermission = this.isAdmin || this.acl.clientAdmin || this.acl.ro;
      if (!hasPermission) return;

      this.showTakeId = null;

      if (!outputs || (Array.isArray(outputs) && outputs.length === 0 && !this.selectedOutput)) {
        return;
      }

      // Validate locations for all target outputs
      const targetOutputs = outputs || [this.selectedOutput];
      const outputArray = Array.isArray(targetOutputs) ? targetOutputs : [targetOutputs];
      
      // Check location compatibility for all outputs
      const incompatibleOutputs = outputArray.filter(output => {
        const validation = this.validateInputOutputLocations(input, output);
        return !validation.isValid;
      });

      if (incompatibleOutputs.length > 0) {
        const outputNames = incompatibleOutputs.map(o => o.name).join(', ');
        this.$message.error(`Cannot route to outputs with incompatible locations: ${outputNames}`);
        return;
      }

      if (this.useTake) {
        this.showTakeId = input.id;
        return;
      }

      // Use manual routing for direct input-output connections
      outputArray.forEach((output) => {
        this.executeManualRoute(input, output);
      });
    },

    async sendRouteCommand(input, outputs) {
      // This method is kept for backward compatibility with the take confirmation
      const outputArray = Array.isArray(outputs) ? outputs : [outputs];

      // Use manual routing for each output
      for (const output of outputArray) {
        await this.executeManualRoute(input, output);
      }

      this.showTakeId = null;
      // this.clearOutputsSelection();
    },

    async endRouteCommand(input, outputs) {
      await this.sendRouteCommand(input, outputs);
    },

    // Output selection handling
    handleOutputMouseDown(event, output) {
      if (!event.metaKey && !event.ctrlKey) {
        this.dragStartX = event.clientX;
        this.dragStartY = event.clientY;
      }
    },

    handleOutputClick(event, output) {
      event.preventDefault();

      if (event.metaKey || event.ctrlKey) {
        this.toggleOutputInSelection(output);
      } else {
        this.clearOutputsSelection();
        this.toggleOutput(output);
        this.selectedOutput = output;
      }
    },

    toggleOutput(output) {
      this.showTakeId = null;
      if (this.selectedOutput === output) {
        this.selectedOutput = null;
        return;
      }
      this.selectedOutput = output;
      this.selectedInput = null;
      this.$forceUpdate();

      if (this.keys.Shift) return;

      // Auto-scroll to connected input
      this.$nextTick(() => {
        try {
          // Find the connected input element using the ring-orange-400 class
          const inputsContainer = document.querySelector(".inputs");
          const connectedEl = inputsContainer?.querySelector(".connected");

          if (connectedEl && inputsContainer) {
            inputsContainer.scroll({
              top: connectedEl.offsetTop - 10,
              behavior: "smooth",
            });
          }
        } catch (e) {
          console.warn("No connected inputs found");
        }
      });
    },

    toggleOutputInSelection(output) {
      const index = this.selectedOutputIds.indexOf(output.id);
      if (index === -1) {
        this.selectedOutputIds.push(output.id);
      } else {
        this.selectedOutputIds.splice(index, 1);
      }
      this.selectedOutput = output;
    },

    clearOutputsSelection() {
      this.selectedOutputIds = [];
      this.selectedOutput = null;
    },

    // Drag selection
    startDragSelect(event) {
      if (event.metaKey || event.ctrlKey || event.button !== 0) return;

      this.isDragging = true;
      this.dragStartX = event.clientX;
      this.dragStartY = event.clientY;
      this.dragEndX = event.clientX;
      this.dragEndY = event.clientY;

      if (!event.shiftKey) {
        this.clearOutputsSelection();
        this.selectedOutput = null;
      }
    },

    updateDragSelect(event) {
      if (!this.isDragging) return;

      this.dragEndX = event.clientX;
      this.dragEndY = event.clientY;
    },

    endDragSelect() {
      if (!this.isDragging) return;

      this.selectOutputsInDragArea();
      this.isDragging = false;
    },

    cancelDragSelect() {
      this.isDragging = false;
    },

    selectOutputsInDragArea() {
      const left = Math.min(this.dragStartX, this.dragEndX);
      const top = Math.min(this.dragStartY, this.dragEndY);
      const right = Math.max(this.dragStartX, this.dragEndX);
      const bottom = Math.max(this.dragStartY, this.dragEndY);

      // Minimum size threshold to prevent accidental selection
      if (right - left < 5 || bottom - top < 5) return;

      this.filteredOutputs.forEach((output) => {
        const componentRef = this.$refs[`output-${output.id}`];
        if (!componentRef || !componentRef[0]) return;
        
        // Get the actual DOM element from the component
        const el = componentRef[0].getElement();
        if (!el) return;

        const rect = el.getBoundingClientRect();

        if (rect.left <= right && rect.right >= left && rect.top <= bottom && rect.bottom >= top) {
          if (!this.selectedOutputIds.includes(output.id)) {
            this.selectedOutputIds.push(output.id);
          }
          this.selectedOutput = output;
        }
      });
    },

    // Search and filter handlers
    searchOutputChanged() {
      this.clearOutputsSelection();
      localStorage.setItem("router_searchOutput", this.searchOutput);
    },

    searchInputChanged() {
      localStorage.setItem("router_searchInput", this.searchInput);
    },

    // Thumbnail Management
    /**
     * Initialize thumbnail refresh interval
     * Fetches thumbnails for all inputs every 15 seconds using batch API
     */
    initThumbnailRefresh() {
      // Initial thumbnail fetch
      this.fetchInputThumbnails();

      // Set up recurring thumbnail refresh every 15 seconds
      this.thumbnailRefreshInterval = setInterval(() => {
        this.fetchInputThumbnails();
      }, this.thumbnailRefreshDelay);
    },

    /**
     * Fetch thumbnails for all inputs using batch API
     * Optimized to avoid excessive network requests
     */
    async fetchInputThumbnails() {
      if (!this.inputs || this.inputs.length === 0) return;
      
      // Prevent overlapping batch requests
      if (this.thumbnailBatchLoading) {
        console.log('Thumbnail batch request already in progress, skipping...');
        return;
      }

      this.thumbnailBatchLoading = true;
      this.lastThumbnailFetch = new Date();

      try {
        const probeInputs = [];
        const routerInputs = [];

        this.filteredInputs.forEach(input => {
          if (input.probeProjectId && input.probeTaskId) {
            probeInputs.push(input);
          } else {
            routerInputs.push(input);
          }
        });

        const promises = [];

        if (probeInputs.length > 0) {
          const probeTask = async () => {
            try {
              const projects = probeInputs.map(input => ({
                projectId: input.probeProjectId,
                taskId: input.probeTaskId
              }));
              const Servers = (await import('@/services/Servers')).default;
              const response = await Servers.getInputThumbs({ projects });
              if (response?.data) {
                this.processBatchThumbnailResponse(response.data);
              }
            } catch (error) {
              console.error('Error fetching probe thumbnails:', error);
            }
          };
          promises.push(probeTask());
        }

        if (routerInputs.length > 0) {
          const routerTask = async () => {
            try {
              const inputIds = routerInputs.map(i => i.id);
              const thumbs = await this.fetchRouterThumbnails(inputIds);
              this.processRouterThumbnailResponse(thumbs);
            } catch (error) {
              console.error('Error fetching router thumbnails:', error);
            }
          };
          promises.push(routerTask());
        }

        await Promise.all(promises);

      } catch (error) {
        console.error('Error fetching input thumbnails:', error);
      } finally {
        this.thumbnailBatchLoading = false;
      }
    },

    /**
     * Process batch thumbnail response and update thumbnail data
     * @param {Object} thumbsData - Response data from batch API
     */
    processBatchThumbnailResponse(thumbsData) {
      if (!thumbsData || typeof thumbsData !== 'object') return;

      this.inputs.forEach(input => {
        // Skip if this input was handled by router thumbnails (or just check if key exists)
        if (!input.probeProjectId && !input.probeTaskId) return;

        const inputKey = input.probeProjectId || input.id;
        const thumbnailData = thumbsData[inputKey];

        // Only update if we have data or if we are sure it should be here
        if (typeof thumbnailData !== 'undefined') {
            console.log(`Processing thumbnail for input ${input.id} : ${input.probeTaskId} : (${inputKey})`, thumbnailData);
            if (thumbnailData) {
              this.$set(this.inputThumbnails, input.id, {
                data: thumbnailData[input.probeTaskId]?.data,
                timestamp: new Date(),
                loading: false,
                error: null
              });
            } else {
              this.$set(this.inputThumbnails, input.id, {
                data: null,
                timestamp: new Date(),
                loading: false,
                error: null
              });
            }
        }
      });
    },

    processRouterThumbnailResponse(thumbsData) {
       if (!thumbsData || typeof thumbsData !== 'object') return;
       
       Object.entries(thumbsData).forEach(([inputId, base64Thumb]) => {
          this.$set(this.inputThumbnails, inputId, {
              data: base64Thumb,
              timestamp: new Date(),
              loading: false,
              error: null
          });
       });
    },

    /**
     * Get thumbnail data for a specific input
     * @param {Object} input - Input object
     * @returns {Object} Thumbnail data with loading/error states
     */
    getThumbnailData(input) {
      const thumbnailData = this.inputThumbnails[input.id];
      
      if (!thumbnailData) {
        return {
          data: null,
          loading: this.thumbnailBatchLoading,
          error: null,
          timestamp: null
        };
      }

      return {
        data: thumbnailData.data,
        loading: thumbnailData.loading || this.thumbnailBatchLoading,
        error: thumbnailData.error,
        timestamp: thumbnailData.timestamp
      };
    },

    // Handler for disconnect route events from output items
    async handleDisconnectRoute(output) {
      if (!output.routedInput) {
        this.$message.warning("No route to disconnect for this output");
        return;
      }

      // Additional safety check: ensure the output is actually connected
      if (!output.routedInput.id) {
        this.$message.warning("Output is not currently routed to any input");
        return;
      }

      await this.disconnectRoute(output.routedInput, output);
    },

    // Handler for disconnect all routes events from input items
    async handleDisconnectAllRoutes(input) {
      const connectedOutputs = this.outputs.filter(output => output.routedInput?.id === input.id);
      
      if (connectedOutputs.length === 0) {
        this.$message.warning("No routes to disconnect for this input");
        return;
      }

      // Additional safety check: double-verify connected outputs exist
      const actuallyConnectedOutputs = connectedOutputs.filter(output => 
        output.routedInput && output.routedInput.id === input.id
      );

      if (actuallyConnectedOutputs.length === 0) {
        this.$message.warning("No active routes found for this input");
        return;
      }

      if (actuallyConnectedOutputs.length !== connectedOutputs.length) {
        console.warn("Mismatch in connected outputs count. Using verified list.");
      }

      await this.disconnectAllRoutesFromInput(input, actuallyConnectedOutputs);
    },

    // New method for disconnecting all routes from an input using routerRouteOut mutation
    async disconnectAllRoutesFromInput(input, connectedOutputs) {
      const hasPermission = this.isAdmin || this.acl.clientAdmin || this.acl.ro;
      if (!hasPermission) {
        this.$message.error("Insufficient permissions to disconnect routes");
        return;
      }

      try {
        const outputNames = connectedOutputs.map(o => o.name).join(', ');
        
        // Enhanced confirmation dialog for bulk disconnection with stronger warnings
        await this.$confirm(
          `⚠️ BULK DISCONNECT WARNING ⚠️\n\nYou are about to disconnect ALL routes from input "${input.name}".\n\n` +
          `This will affect ${connectedOutputs.length} output(s):\n${outputNames}\n\n` +
          `⚠️ This action may interrupt live streams and active broadcasts!\n\n` +
          `Are you absolutely sure you want to proceed?`,
          'Confirm Bulk Route Disconnection',
          {
            confirmButtonText: 'Yes, Disconnect All',
            cancelButtonText: 'Cancel',
            type: 'warning',
            confirmButtonClass: 'el-button--danger',
            dangerouslyUseHTMLString: false,
            distinguishCancelAndClose: true
          }
        );

        // Check for live event conflicts on any connected outputs
        const liveEventOutputs = [];
        for (const output of connectedOutputs) {
          const conflictCheck = await this.checkLiveEventConflict(output);
          if (conflictCheck.hasConflict) {
            liveEventOutputs.push(output.name);
          }
        }

        if (liveEventOutputs.length > 0) {
          this.liveEventWarningMessage = `⚠️ LIVE EVENT WARNING: Outputs (${liveEventOutputs.join(', ')}) are associated with LIVE events. Disconnecting WILL affect live broadcasts!`;
          this.showLiveEventWarning = true;
          this.pendingRouteOperation = {
            input,
            connectedOutputs,
            operation: () => this.executeInputBulkDisconnection(input)
          };
          return;
        }

        await this.executeInputBulkDisconnection(input);
      } catch (error) {
        if (error !== 'cancel') {
          console.error("Bulk route disconnection error:", error);
          this.$message.error("Failed to disconnect routes");
        }
      }
    },

    async executeInputBulkDisconnection(input) {
      // Set loading state for the input and all connected outputs
      this.setInputLoading(input.id, true);
      const connectedOutputs = this.outputs.filter(output => output.routedInput?.id === input.id);
      connectedOutputs.forEach(output => this.setOutputLoading(output.id, true));

      try {
        // For input-level bulk disconnection, only send inputId
        const result = await this.routerRouteOut({
          inputId: input.id
          // Note: outputId is intentionally omitted for input-level bulk disconnects
        });

        if (result.success) {
          const connectedCount = connectedOutputs.length;
          this.$message.success(result.message || `All routes disconnected from "${input.name}" (${connectedCount} outputs affected)`);
          // Refresh data to show updated connections
          await Promise.all([this.fetchInputs(), this.fetchOutputs()]);
        } else {
          this.$message.error(result.message || "Failed to disconnect routes");
        }
      } catch (error) {
        console.error("Execute input bulk disconnection error:", error);
        this.$message.error("Failed to disconnect routes");
      } finally {
        // Clear loading states regardless of success/failure
        this.setInputLoading(input.id, false);
        connectedOutputs.forEach(output => this.setOutputLoading(output.id, false));
      }
    },
  },
};
</script>

<style scoped>
/* Custom element UI styles */
.el-input input {
  background: transparent !important;
  border-color: transparent !important;
  color: #cdd6ee !important;
}

/* Loading spinner centering */
.el-loading-spinner svg {
  margin: 0 auto;
}

/* Width utilities for specific components */

/* Ring colors for active states - matching original design */
.ring-orange-400 {
  --tw-ring-color: #ff9f31;
}

.ring-blue-400 {
  --tw-ring-color: #00d4ff;
}

.ring-green-400 {
  --tw-ring-color: #00ff80;
}

/* Connected item text color override */
.ring-orange-400 .text-slate-200 {
  color: #ff9f31 !important;
}

.ring-blue-400 .text-slate-200 {
  color: #ff9f31 !important;
}

/* Connected item avatar override */
.ring-orange-400 .el-avatar--square,
.ring-blue-400 .el-avatar--square {
  background: #ff9f31 !important;
}

/* Hover button overlay enhancements */
.group:hover .group-hover\:opacity-100 {
  opacity: 1 !important;
}

.group:hover .group-hover\:visible {
  visibility: visible !important;
}

.group:hover .group-hover\:translate-x-0 {
  transform: translateX(0) !important;
}

/* Focus ring styles for better accessibility */
.el-button:focus {
  outline: none !important;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5) !important;
}

.el-button.is-circle:focus {
  border-radius: 50% !important;
}

/* Ensure proper z-index for hover overlays */
.absolute.opacity-0 {
  z-index: 10;
}

/* Smooth transitions for all interactive elements */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

/* Hover overlay backdrop blur for better text readability */
.bg-linear-to-l {
  backdrop-filter: blur(2px);
}

/* Input item hover overlay specific styling */
.group .absolute.opacity-0 {
  pointer-events: none;
}

.group:hover .absolute.opacity-0 {
  pointer-events: auto;
}

/* TAKE button specific styling */
.el-button--primary.el-button--mini:not(.is-circle) {
  background-color: #1d4ed8 !important;
  border-color: #1d4ed8 !important;
  color: white !important;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
}

.el-button--primary.el-button--mini:not(.is-circle):hover {
  background-color: #1e40af !important;
  border-color: #1e40af !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1) !important;
}

.el-button--primary.el-button--mini:not(.is-circle):disabled {
  background-color: #6b7280 !important;
  border-color: #6b7280 !important;
  color: #9ca3af !important;
  cursor: not-allowed !important;
  transform: none !important;
  box-shadow: none !important;
}

/* Action button spacing and layout */
.group .flex.items-center.space-x-2 > * {
  flex-shrink: 0;
}

/* Button hover enhancements */
.el-button--mini.is-circle {
  width: 24px !important;
  height: 24px !important;
  padding: 0 !important;
  font-size: 12px !important;
}

/* Wide TAKE button styling */
.el-button--mini:not(.is-circle) {
  height: 24px !important;
  padding: 0 8px !important;
  font-size: 11px !important;
  font-weight: 600 !important;
  letter-spacing: 0.025em !important;
}

/* Enhanced hover overlay styling */
.group:hover .group-hover\:opacity-100 {
  opacity: 1 !important;
}

.group:hover .group-hover\:visible {
  visibility: visible !important;
}

.group:hover .group-hover\:translate-x-0 {
  transform: translateX(0) !important;
}

/* Loading state improvements */
.absolute.inset-1 {
  border-radius: 6px;
}

/* Responsive button sizes */
@media (max-width: 768px) {
  .el-button--mini.is-circle {
    width: 20px !important;
    height: 20px !important;
    font-size: 10px !important;
  }

  .el-button--mini:not(.is-circle) {
    height: 20px !important;
    padding: 0 6px !important;
    font-size: 10px !important;
    min-width: 40px !important;
  }
}

/* Unrouted elements styling */
.bg-slate-800.opacity-60 {
  border: 1px solid #4b5563;
}

.bg-slate-800.opacity-60 .text-slate-200 {
  color: #9ca3af !important;
}

.bg-slate-800.opacity-60 .text-blue-400 {
  color: #6b7280 !important;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .w-63 {
    width: 150px !important;
  }

  .h-13 {
    height: auto !important;
  }

  /* Show action buttons on mobile by default since hover doesn't work */
  .group .opacity-0 {
    opacity: 0.9 !important;
    visibility: visible !important;
    transform: translateX(0) !important;
  }

  .group .opacity-0:hover {
    opacity: 1 !important;
  }

  /* Adjust button sizes for touch targets */
  .el-button--mini.is-circle {
    width: 28px !important;
    height: 28px !important;
    min-width: 28px !important;
    padding: 0 !important;
  }

  .el-button--mini:not(.is-circle) {
    height: 28px !important;
    padding: 0 8px !important;
    font-size: 11px !important;
    min-width: 50px !important;
  }

  /* Ensure proper spacing on mobile */
  .text-white.text-sm {
    padding-right: 120px !important;
  }
}

/* Tablet and small desktop hover improvements */
@media (min-width: 769px) and (max-width: 1024px) {
  .group .opacity-0 {
    opacity: 0.7 !important;
    visibility: visible !important;
    transform: translateX(0) !important;
  }

  .group:hover .opacity-0 {
    opacity: 1 !important;
  }

  /* Tablet button sizing */
  .el-button--mini:not(.is-circle) {
    height: 22px !important;
    padding: 0 7px !important;
    font-size: 10px !important;
    min-width: 45px !important;
  }
}
</style>

